import React from 'react';
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TrendingUp, Cloud, CloudRain, Sun, CloudSnow, CloudLightning, CloudDrizzle, CloudFog } from 'lucide-react';
import { useQuery } from "@tanstack/react-query";
import { base44 } from "api/apiClient";
import { useRestaurant } from "../../contexts/RestaurantContext";

/**
 * Map WMO weather code to a Lucide icon component.
 */
function getWeatherIcon(code, className = "w-4 h-4") {
  if (code === 0) return <Sun className={className} />;
  if (code >= 1 && code <= 3) return <Cloud className={className} />;
  if (code >= 45 && code <= 48) return <CloudFog className={className} />;
  if (code >= 51 && code <= 57) return <CloudDrizzle className={className} />;
  if (code >= 61 && code <= 67) return <CloudRain className={className} />;
  if (code >= 71 && code <= 77) return <CloudSnow className={className} />;
  if (code >= 80 && code <= 82) return <CloudRain className={className} />;
  if (code >= 95 && code <= 99) return <CloudLightning className={className} />;
  return <Cloud className={className} />;
}

export default function RevenueForecast() {
  const { t } = useTranslation();
  const { currentRestaurantId } = useRestaurant();
  const restaurantQueryKey = currentRestaurantId ?? 'default';

  const { data: forecastResponse, isLoading } = useQuery({
    queryKey: ['forecast', 'revenue', restaurantQueryKey, 7],
    queryFn: () => base44.forecast.getRevenue(7),
    placeholderData: { forecasts: [], meta: {} },
    staleTime: 5 * 60 * 1000,
  });

  const forecasts = forecastResponse?.forecasts || [];

  // Day name short mapping
  const dayNamesShort = {
    'Montag': 'Mo', 'Dienstag': 'Di', 'Mittwoch': 'Mi',
    'Donnerstag': 'Do', 'Freitag': 'Fr', 'Samstag': 'Sa', 'Sonntag': 'So',
  };

  const revenueData = forecasts.map(f => ({
    dayName: dayNamesShort[f.day_name] || f.day_name.slice(0, 2),
    date: f.date ? f.date.slice(5).replace('-', '.') : '',
    revenue: f.is_closed ? 0 : (f.revenue || 0),
    isClosed: f.is_closed,
    weather: f.weather,
  }));

  const totalForecast = revenueData.reduce((sum, f) => sum + f.revenue, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          {t("dashboard.revenueForecast7Days")}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="text-center py-8 text-slate-400">{t("dashboard.loadingForecast")}</div>
        ) : (
          <>
            <div className="mb-6">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-slate-900">EUR {totalForecast.toFixed(2)}</span>
                <span className="text-sm text-slate-500">{t("dashboard.totalForecast")}</span>
              </div>
            </div>
            <div className="space-y-3">
              {revenueData.map((forecast, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    {forecast.weather && (
                      <span className="text-slate-400">
                        {getWeatherIcon(forecast.weather.weather_code, "w-4 h-4")}
                      </span>
                    )}
                    <div>
                      <p className="font-semibold text-slate-900">{forecast.dayName}, {forecast.date}</p>
                      {forecast.weather && (
                        <p className="text-xs text-slate-400">{forecast.weather.temp_max}C</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    {forecast.isClosed ? (
                      <p className="text-sm text-slate-400">{t("dashboard.restaurantClosed")}</p>
                    ) : (
                      <p className="text-lg font-bold text-green-600">EUR {forecast.revenue.toFixed(2)}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
